import {useEffect, useState} from 'react';

import {ProvidersMedicalGuide} from 'domain/usecases';
import {GuideFilterEnum} from 'main/analytics/gaTagsEnum';
import {FeaturesEnum, PlanTypeEnum, QueryParamsEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useQueryParams} from 'presentation/pages/private';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {getUserInfoFromToken} from 'presentation/utils';
import {useGuideStore} from 'presentation/modules/guide/shared/store/guideStore';
import {SortOrderType} from 'presentation/modules/guide/shared/store/guideStore/types';
import {DEFAULT_DISTANCE, DISTANCE_MARKS, DISTANCE_VALUES} from '../../constants';
import {District} from '../../types';
import {normalizeDistrictKey} from 'presentation/modules/guide/shared/utils/normalizeDistrictKey';
import {useDistrictsQuery} from 'presentation/modules/guide/shared/hooks/useDistrictsQuery';
import {UseSearchBarUnitProps} from './types';
import {getUniqueDistricts} from 'presentation/modules/guide/shared/utils/getUniqueDistricts';

export function useSearchBarUnit({providers, type}: UseSearchBarUnitProps) {
	const searchByProviderName = useGuideStore(state => state.searchByProviderName);
	const setSearchByProviderName = useGuideStore(state => state.setSearchByProviderName);
	const {SEARCH_PARAMS, setSearchParams} = useQueryParams();
	const {data: healthCard, isLoading: healthCardIsLoading} = useGetHealthCardQuery();
	const cdPlanoANSOdonto =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns;
	const {ufCode, cityName, coordinates, orderBy, distance, districtNames} = SEARCH_PARAMS;

	const [openDrawerState, setOpenDrawerState] = useState(false);
	const [localSelectDistrict, setLocalSelectDistrict] = useState('');
	const [localSortOrder, setLocalSortOrder] = useState<SortOrderType>(SortOrderType.NONE);

	const {isEnabled: isDistanceFeatureEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_DISTANCE_MEDICAL_GUIDE,
	);

	const providerName = searchByProviderName[type];

	const cdPessoaC = getUserInfoFromToken().cdPessoa;
	const getLocation = useLocationStore(state => state.getLocation);
	const setLocation = useLocationStore(state => state.setLocation);
	const location = getLocation(
		cdPessoaC,
		type === PlanTypeEnum.ODONTO ? FlowType.ODONTO_GUIDE : FlowType.HEALTH_GUIDE,
	);

	const isHealthGuide = type === PlanTypeEnum.HEALTH;

	const defaultSelectedDistance = distance || DEFAULT_DISTANCE;
	const defaultDistanceSliderIndex = DISTANCE_MARKS.findIndex(
		mark => mark.label === `${defaultSelectedDistance}km`,
	);
	const safeDefaultDistanceSliderValue =
		defaultDistanceSliderIndex === -1 ? 0 : defaultDistanceSliderIndex;

	const [distanceSliderValue, setDistanceSliderValue] = useState(safeDefaultDistanceSliderValue);
	const [selectedDistance, setSelectedDistance] = useState(defaultSelectedDistance);
	const distanceIndex = DISTANCE_MARKS.findIndex(mark => mark.value === distanceSliderValue);

	const isGeoFlow = !!coordinates;

	useEffect(() => {
		if (safeDefaultDistanceSliderValue !== distanceSliderValue) {
			setDistanceSliderValue(safeDefaultDistanceSliderValue);
		}
		if (defaultSelectedDistance !== selectedDistance) {
			setSelectedDistance(defaultSelectedDistance);
		}
	}, [safeDefaultDistanceSliderValue, defaultSelectedDistance]);

	const filterDistrictsByProviders = (districts: District[]): District[] => {
		if (type === PlanTypeEnum.ODONTO) {
			return getUniqueDistricts(districts);
		}

		const buildAvailableDistricts = (): Set<string> => {
			const medicalProviders = (providers as ProvidersMedicalGuide[]) ?? [];

			return new Set(
				medicalProviders
					.filter(p => p?.cdUf && p?.nmCidade && p?.nmBairro)
					.map(p => normalizeDistrictKey(p?.cdUf, p?.nmCidade, p?.nmBairro)),
			);
		};

		const availableDistricts = buildAvailableDistricts();
		const seen = new Set<string>();

		return districts.filter(district => {
			const key = normalizeDistrictKey(district?.uf, district?.cidade, district?.bairro);

			if (!availableDistricts.has(key)) return false;
			if (seen.has(key)) return false;

			seen.add(key);
			return true;
		});
	};

	const {data: districtList, isLoading: districtListIsLoading} = useDistrictsQuery({
		flowType: type === PlanTypeEnum.ODONTO ? FlowType.ODONTO_GUIDE : FlowType.HEALTH_GUIDE,
		healthParams: {
			cdUf: ufCode,
			nmCidade: cityName,
			coordenadas: coordinates || undefined,
			enabled: false,
		},
		odontoParams: {
			cdUf: ufCode,
			nmCidade: cityName,
			nmLocal: coordinates,
			cdPlanoANS: cdPlanoANSOdonto ?? '',
			enabled: openDrawerState && type === PlanTypeEnum.ODONTO,
		},
	});

	const providerGuideTagging =
		type === PlanTypeEnum.ODONTO
			? GuideFilterEnum.APLICOU_FILTRO_PRESTADOR_ODONTO
			: GuideFilterEnum.APLICOU_FILTRO_PRESTADOR_SAUDE;

	const handleOpenDrawer = () => {
		setOpenDrawerState(true);
		setLocalSortOrder(orderBy as SortOrderType);
	};

	const handleCloseDrawer = () => {
		setOpenDrawerState(false);
		setLocalSortOrder(SortOrderType.NONE);
		setDistanceSliderValue(safeDefaultDistanceSliderValue);
		setSelectedDistance(defaultSelectedDistance);
	};

	const handleDistrictSelection = (selectedDistrictName: string) => {
		if (!selectedDistrictName) return;
		setLocalSelectDistrict(selectedDistrictName);
	};

	const handleApplyFilter = () => {
		const flowType =
			type === PlanTypeEnum.ODONTO ? FlowType.ODONTO_GUIDE : FlowType.HEALTH_GUIDE;

		const updatedLocation = {
			...location,
			districtNames: localSelectDistrict ? [localSelectDistrict] : [],
		};

		setLocation(cdPessoaC, flowType, updatedLocation);
		setSearchParams(prev => {
			prev.set(QueryParamsEnum.DISTRICT_NAMES, localSelectDistrict);
			prev.set(QueryParamsEnum.ORDER_BY, localSortOrder);
			if (isDistanceFeatureEnabled && isHealthGuide) {
				prev.set(QueryParamsEnum.DISTANCE, selectedDistance);
			}
			return prev;
		});

		handleCloseDrawer();
	};

	const handleClearFilter = () => {
		const flowType =
			type === PlanTypeEnum.ODONTO ? FlowType.ODONTO_GUIDE : FlowType.HEALTH_GUIDE;

		const updatedLocation = {
			...location,
			districtNames: [],
		};

		setLocation(cdPessoaC, flowType, updatedLocation);
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.DISTRICT_NAMES);
			prev.set(QueryParamsEnum.ORDER_BY, SortOrderType.NONE);
			if (isDistanceFeatureEnabled && isHealthGuide) {
				prev.set(QueryParamsEnum.DISTANCE, DEFAULT_DISTANCE);
			}
			return prev;
		});

		setLocalSelectDistrict('');
		handleCloseDrawer();
	};

	const handleChangeDistance = (event: Event, newValue: number | number[]) => {
		const value = Number(newValue);
		setDistanceSliderValue(value);
		setSelectedDistance(DISTANCE_VALUES[value]);
	};

	const formatDistrictValue = (bairro?: string, cidade?: string, uf?: string) => {
		if (type === PlanTypeEnum.ODONTO && isGeoFlow) return `${bairro}-${cidade}-${uf}`;
		if (isGeoFlow) return `${bairro} - ${cidade}`;
		return bairro;
	};

	const activeFiltersCount =
		(localSelectDistrict || districtNames ? 1 : 0) +
		(orderBy && orderBy !== SortOrderType.NONE ? 1 : 0);

	return {
		setSearchByProviderName,
		providerName,
		handleOpenDrawer,
		openDrawerState,
		districtList,
		districtListIsLoading,
		filterDistrictsByProviders,
		formatDistrictValue,
		localSelectDistrict,
		handleDistrictSelection,
		localSortOrder,
		setLocalSortOrder,
		handleApplyFilter,
		handleClearFilter,
		isDistanceFeatureEnabled,
		isHealthGuide,
		distanceSliderValue,
		handleChangeDistance,
		DISTANCE_MARKS,
		selectedDistance,
		activeFiltersCount,
		providerGuideTagging,
		distanceIndex,
		healthCardIsLoading,
		handleCloseDrawer,
		coordinates,
		cityName,
		isGeoFlow,
		districtNames,
	};
}
